import { z } from 'zod';
import { emailSchema, idSchema } from './common.js';
// -------- Customers --------
const phoneE164Schema = z
    .string()
    .trim()
    .regex(/^\+?[1-9]\d{6,14}$/, 'Phone must be E.164 (e.g. +447700900123)')
    .max(16);
const emailLikeSchema = z.string().trim().email().max(160);
/**
 * The billing block (columns since 0039), shared by create and update.
 *
 * ONE definition on purpose. These fields are what an invoice FREEZES into
 * customer_snapshot at issue, and a snapshot cannot be corrected afterwards —
 * the remedy is a credit note. So the two write paths must not be able to
 * disagree about what is acceptable: a country coerced to upper case on edit
 * but stored lower case on create would fail the DB's ISO-3166 CHECK on one
 * path and pass on the other, and the difference would only show up on a
 * document that is already immutable.
 *
 * Lengths mirror the columns; country mirrors the DB CHECK ('^[A-Z]{2}$').
 * Every field is OPTIONAL, including at create: a dispatcher booking a
 * same-day job must never be blocked from adding a customer because the
 * billing block is not to hand. The guard for that belongs at ISSUE, where
 * the document becomes permanent — not at the point of typing a name in.
 */
const billingBlockShape = {
    vatNumber: z.string().trim().max(20).nullable().optional(),
    billingEmail: emailLikeSchema.nullable().optional(),
    billingAddressLine1: z.string().trim().max(160).nullable().optional(),
    billingAddressLine2: z.string().trim().max(160).nullable().optional(),
    billingCity: z.string().trim().max(80).nullable().optional(),
    billingPostcode: z.string().trim().max(16).nullable().optional(),
    billingCountry: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/, 'Country must be a 2-letter ISO code, e.g. GB')
        .nullable()
        .optional(),
};
export const createCustomerBodySchema = z.object({
    name: z.string().trim().min(1).max(160),
    companyNumber: z.string().trim().max(20).nullable().optional(),
    accountRef: z.string().trim().max(40).nullable().optional(),
    primaryEmail: emailLikeSchema.nullable().optional(),
    primaryPhoneE164: phoneE164Schema.nullable().optional(),
    defaultPaymentTermsDays: z.number().int().min(0).max(365).optional(),
    creditLimitPence: z.number().int().min(0).nullable().optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
    ...billingBlockShape,
});
export const updateCustomerBodySchema = z
    .object({
    name: z.string().trim().min(1).max(160).optional(),
    companyNumber: z.string().trim().max(20).nullable().optional(),
    accountRef: z.string().trim().max(40).nullable().optional(),
    primaryEmail: emailLikeSchema.nullable().optional(),
    primaryPhoneE164: phoneE164Schema.nullable().optional(),
    defaultPaymentTermsDays: z.number().int().min(0).max(365).optional(),
    creditLimitPence: z.number().int().min(0).nullable().optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
    isArchived: z.boolean().optional(),
    // Same billing block as create — see billingBlockShape. Until M2a this
    // .strict() schema carried none of these, so the block was readable and
    // stored but unwritable through the API by anyone.
    ...billingBlockShape,
})
    .strict();
export const listCustomersQuerySchema = z.object({
    search: z.string().trim().max(120).optional(),
    /** M2a: only customers a VAT invoice could not currently be issued for. */
    billingIncomplete: z
        .union([z.literal('true'), z.literal('false'), z.boolean()])
        .optional()
        .transform((v) => v === true || v === 'true'),
    includeArchived: z
        .union([z.literal('true'), z.literal('false'), z.boolean()])
        .optional()
        .transform((v) => v === true || v === 'true'),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const customerIdParamSchema = z.object({
    fleetId: idSchema,
    customerId: idSchema,
});
// -------- Customer contacts --------
export const createContactBodySchema = z.object({
    fullName: z.string().trim().min(1).max(120),
    role: z.string().trim().max(60).nullable().optional(),
    email: emailLikeSchema.nullable().optional(),
    phoneE164: phoneE164Schema.nullable().optional(),
    isPrimary: z.boolean().optional(),
});
export const updateContactBodySchema = z
    .object({
    fullName: z.string().trim().min(1).max(120).optional(),
    role: z.string().trim().max(60).nullable().optional(),
    email: emailLikeSchema.nullable().optional(),
    phoneE164: phoneE164Schema.nullable().optional(),
    isPrimary: z.boolean().optional(),
})
    .strict();
export const contactIdParamSchema = z.object({
    fleetId: idSchema,
    customerId: idSchema,
    contactId: idSchema,
});
// -------- Customer addresses --------
const ukOrIsoCountrySchema = z
    .string()
    .trim()
    .toUpperCase()
    .pipe(z.string().regex(/^[A-Z]{2}$/, 'country must be ISO-2 (e.g. GB)'));
const latitudeSchema = z.number().min(-90).max(90);
const longitudeSchema = z.number().min(-180).max(180);
const openingHoursSchema = z
    .object({
    mon: z.string().max(40).optional(),
    tue: z.string().max(40).optional(),
    wed: z.string().max(40).optional(),
    thu: z.string().max(40).optional(),
    fri: z.string().max(40).optional(),
    sat: z.string().max(40).optional(),
    sun: z.string().max(40).optional(),
})
    .strict();
export const createAddressBodySchema = z
    .object({
    label: z.string().trim().max(80).nullable().optional(),
    line1: z.string().trim().min(1).max(160),
    line2: z.string().trim().max(160).nullable().optional(),
    city: z.string().trim().max(80).nullable().optional(),
    postcode: z.string().trim().max(16).nullable().optional(),
    country: ukOrIsoCountrySchema.optional(),
    what3words: z.string().trim().max(80).nullable().optional(),
    lat: latitudeSchema.nullable().optional(),
    lng: longitudeSchema.nullable().optional(),
    accessNotes: z.string().trim().max(2000).nullable().optional(),
    contactName: z.string().trim().max(120).nullable().optional(),
    contactPhoneE164: phoneE164Schema.nullable().optional(),
    openingHours: openingHoursSchema.nullable().optional(),
})
    .superRefine((v, ctx) => {
    // lat without lng (or vice versa) is almost always a bug — reject early.
    const hasLat = v.lat !== null && v.lat !== undefined;
    const hasLng = v.lng !== null && v.lng !== undefined;
    if (hasLat !== hasLng) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: hasLat ? ['lng'] : ['lat'],
            message: 'lat and lng must be provided together',
        });
    }
});
export const updateAddressBodySchema = z
    .object({
    label: z.string().trim().max(80).nullable().optional(),
    line1: z.string().trim().min(1).max(160).optional(),
    line2: z.string().trim().max(160).nullable().optional(),
    city: z.string().trim().max(80).nullable().optional(),
    postcode: z.string().trim().max(16).nullable().optional(),
    country: ukOrIsoCountrySchema.optional(),
    what3words: z.string().trim().max(80).nullable().optional(),
    lat: latitudeSchema.nullable().optional(),
    lng: longitudeSchema.nullable().optional(),
    accessNotes: z.string().trim().max(2000).nullable().optional(),
    contactName: z.string().trim().max(120).nullable().optional(),
    contactPhoneE164: phoneE164Schema.nullable().optional(),
    openingHours: openingHoursSchema.nullable().optional(),
})
    .strict();
export const addressIdParamSchema = z.object({
    fleetId: idSchema,
    customerId: idSchema,
    addressId: idSchema,
});
/** Dispatcher invites a person (by email) to this customer's portal access. */
export const createCustomerPortalInviteBodySchema = z
    .object({ email: emailSchema })
    .strict();
//# sourceMappingURL=customer.js.map